import { Show, For } from 'solid-js'
import coinPng from '~/assets/figma/coin.png'
import dividerLeftSvg from '~/assets/figma/divider-left.svg'
import dividerRightSvg from '~/assets/figma/divider-right.svg'
import gemPng from '~/assets/figma/gem.png'
import { CakeBoxIcon } from '~/components/icon/cake-box'
import type { ExtraBoxes, ExtraBoxOption } from '~/lib/derive-extra-boxes'
import { m } from '~/paraglide/messages'

type RowConfig = {
  currency: 'coin' | 'gem'
  option: ExtraBoxOption | undefined
}

export function ExtraBoxesSection(props: {
  boxes: ExtraBoxes
  onPick: (currency: 'coin' | 'gem', option: ExtraBoxOption) => void
}) {
  const rows = (): RowConfig[] => [
    { currency: 'gem', option: props.boxes.gem },
    { currency: 'coin', option: props.boxes.coin },
  ]
  return (
    <div class="flex w-full flex-col items-center gap-3">
      <div class="flex h-[19px] items-center justify-center gap-2">
        <img
          src={dividerLeftSvg}
          alt=""
          aria-hidden="true"
          class="h-[7px] w-16 -scale-y-100"
        />
        <span class="whitespace-nowrap text-sm font-semibold leading-5 text-bakery-brown-soft">
          {m.daily_extra_title()}
        </span>
        <img
          src={dividerRightSvg}
          alt=""
          aria-hidden="true"
          class="h-[7px] w-16 rotate-180"
        />
      </div>
      <div class="flex w-full items-center gap-3 max-[375px]:gap-1">
        <For each={rows()}>
          {(row) => (
            <Show when={row.option} fallback={<ExtraBoxPlaceholder />}>
              {(opt) => (
                <ExtraBoxCard
                  currency={row.currency}
                  option={opt()}
                  onPick={() => props.onPick(row.currency, opt())}
                />
              )}
            </Show>
          )}
        </For>
      </div>
    </div>
  )
}

/** Reserve the row's full height (52px button + 16px "N left" line) while
 *  the extra-box product / stats queries are still loading so the section
 *  doesn't pop in and shift everything below it. */
function ExtraBoxPlaceholder() {
  return (
    <div class="flex flex-1 flex-col items-center gap-1">
      <div
        aria-hidden
        class="h-[52px] w-full animate-pulse rounded-2xl bg-white/50"
      />
      <span aria-hidden class="text-xs leading-4 opacity-0">
        —
      </span>
    </div>
  )
}

function ExtraBoxCard(props: {
  currency: 'coin' | 'gem'
  option: ExtraBoxOption
  onPick: () => void
}) {
  const soldOut = () => props.option.remaining <= 0
  return (
    <div class="flex flex-1 flex-col items-center gap-1">
      <button
        type="button"
        disabled={soldOut()}
        onClick={soldOut() ? undefined : props.onPick}
        class="flex h-[52px] w-full items-center justify-center gap-3 overflow-hidden rounded-2xl bg-white p-2 transition-colors duration-100 max-[421px]:gap-1"
        classList={{
          '[&>*]:opacity-50 cursor-not-allowed': soldOut(),
          'active:bg-bakery-cream': !soldOut(),
        }}
      >
        <div class="flex items-center gap-1 max-[421px]:gap-0.5">
          <CakeBoxIcon class="size-[34px]" />
          <span class="whitespace-nowrap text-[clamp(11px,3.5vw,16px)] font-semibold tabular-nums leading-[22px] text-bakery-brown">
            1
          </span>
        </div>
        <div class="flex items-center gap-1 rounded-[32px] px-2 py-1 max-[421px]:gap-0.5 max-[421px]:px-1">
          <img
            src={props.currency === 'gem' ? gemPng : coinPng}
            alt=""
            aria-hidden="true"
            class="size-5"
          />
          <span class="whitespace-nowrap text-[clamp(11px,3vw,14px)] font-bold tabular-nums leading-[18px] text-bakery-gem-text">
            {props.option.price}
          </span>
        </div>
      </button>
      <span
        class="text-xs leading-4"
        classList={{
          'text-[#d64949]': soldOut(),
          'text-bakery-brown-soft': !soldOut(),
        }}
      >
        {m.number_of_left({
          number: String(props.option.remaining),
        })}
      </span>
    </div>
  )
}
